<template>
    <div class="space-y-6 pb-8">
        <div class="-mb-4 pl-2 text-2xl font-semibold uppercase tracking-wide">
            {{ planTitle }}
        </div>

        <plan-presentation
            v-if="!isEditing"
            :plan="planData"
            :allow-bookings="allowBookings"
        />

        <template v-if="allowBookings">
            <duplicate-contract-message
                :plan-id="planData.id"
                :contract-id="contractData?.id"
                warning-text="You already have a contract for this membership in your cart"
                edit-text="Edit Membership"
            />

            <select-participants
                :is-active-section="activeSection === 0"
                :contract="contractData"
                :plan="planData"
                :is-mutating="isCreatingContract || isChangingContract"
                @create-contract="handleCreateContract"
                @change-contract="handleChangeContract"
                @edit-section="activeSection = 0"
            />

            <template v-if="contractData !== undefined">
                <participant-details
                    v-if="activeSection > 0"
                    :is-active-section="activeSection === 1"
                    :contract="contractData"
                    :plan="planData"
                    :is-editing="isEditing"
                    :is-updating-contract="
                        isUpdatingContract || isActivatingContract
                    "
                    @continue="handleContinue"
                    @add-to-cart="handleAddToCartParticipantDetails"
                    @activate-contract-now="handleActivateContractNow"
                    @edit-section="activeSection = 1"
                />

                <checkout-section
                    v-if="isKiosk && activeSection > 1"
                    :contract="contractData"
                />

                <document-section
                    v-if="!isKiosk && activeSection > 1"
                    :contract="contractData"
                    :is-signing-document="isSigningDocument"
                    :invalid-initials="invalidInitials"
                    :invalid-signatures="invalidSignatures"
                    @add-to-cart="addToCart"
                    @sign-document="handleSignDocument"
                />
            </template>
        </template>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const PurchaseMembershipPlanFragment = graphql(/** GraphQL */ `
    fragment PurchaseMembershipPlanFragment on Plan {
        id
        name
        planType
        ...PlanPresentationPlanFragment
        ...SelectParticipantsPlanFragment
        ...ParticipantDetailsPlanFragment
        presentation(input: $input, language: ENGLISH) {
            title
        }
        checkAllowBookings: bookingLimit(input: $input) {
            allowBookings
        }
    }
`)

export const PurchaseMembershipContractFragment = graphql(/** GraphQL */ `
    fragment PurchaseMembershipContractFragment on PlanContract {
        ...SelectParticipantsContractFragment
        ...ParticipantDetailsContractFragment
        ...DocumentSectionContractFragment
        ...PaymentMethodInputPlanContractFragment
        id
        updatedAtUtc
        draftExpiresAtUtc
        readyForPurchase(input: { checkDocument: true })
    }
`)
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import ParticipantDetails from '~/components/PurchaseContract/ParticipantDetails/ParticipantDetails.vue'
import SelectParticipants from '~/components/PurchaseContract/Membership/SelectParticipants.vue'
import DocumentSection from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import CheckoutSection from '~/components/PurchaseContract/CheckoutSection/CheckoutSection.vue'
import type {
    ChangeContractInput,
    CreateContractInput,
    PurchaseMembershipContractFragmentFragment,
    SignDocumentInput,
    UpdateContractInput
} from '~/resources/graphql/graphql'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import {
    setCartVisible,
    useRedirectOnContractDelete,
    useSetCartData
} from '~/composables/use-cart'
import { useRouter } from 'vue-router'
import DuplicateContractMessage from '~/components/PurchaseContract/DuplicateContractMessage.vue'
import { useRouteLink } from '~/composables/routing'
import PlanPresentation from '~/components/PurchaseContract/PlanPresentation.vue'
import { useMembershipCreateContractMutation } from '~/components/PurchaseContract/Membership/use-membership-create-contract-mutation'
import { useMembershipChangeContractMutation } from '~/components/PurchaseContract/Membership/use-membership-change-contract.mutation'
import { useMembershipUpdateContractMutation } from '~/components/PurchaseContract/Membership/use-membership-update-contract-mutation'
import { useMembershipSignDocumentMutation } from '~/components/PurchaseContract/Membership/use-membership-sign-document-mutation'
import { useActivateContractMutation } from '~/components/PurchaseContract/use-activate-contract-mutation'
import { useToast } from 'primevue/usetoast'
import { useLayoutMode } from '~/composables/use-layout-mode'
import { useStorefrontPrefix } from '~/composables/use-storefront-prefix'
import { useQueryClient } from '@tanstack/vue-query'
import { getEditingActiveSection } from '~/components/PurchaseContract/purchase-contract-helpers'
import { useHead } from 'unhead'
import { useOrgShortTitle } from '~/composables/use-org'
import { useShowAlert } from '#imports'
import { handleValidationErrors } from '~/components/PurchaseContract/DocumentSection/handle-validation-errors'
import { useAddToCartAnalytics } from '~/composables/analytics/add-to-cart'

const props = withDefaults(
    defineProps<{
        plan: FragmentType<typeof PurchaseMembershipPlanFragment>
        initialContract?: FragmentType<
            typeof PurchaseMembershipContractFragment
        >
    }>(),
    {
        initialContract: undefined
    }
)

const planData = computed(() =>
    getFragmentData(PurchaseMembershipPlanFragment, props.plan)
)

const planTitle = computed(() => {
    if (
        planData.value.presentation !== null &&
        planData.value.presentation.title !== ''
    ) {
        return planData.value.presentation.title
    }

    return planData.value.name
})

const shortTitle = useOrgShortTitle()
useHead({
    title: () => shortTitle.value + ' | ' + planTitle.value
})

const { isKiosk } = useLayoutMode()
const { emitAddToCartEvent } = useAddToCartAnalytics()
const isEditing = computed(() => props.initialContract !== undefined)

const allowBookings = computed(
    () => planData.value.checkAllowBookings.allowBookings
)

const contractData = ref<
    PurchaseMembershipContractFragmentFragment | undefined
>(
    props.initialContract !== undefined
        ? getFragmentData(
              PurchaseMembershipContractFragment,
              props.initialContract
          )
        : undefined
)

const activeSection = ref(0)
function setEditingActiveSection() {
    // If we are not editing, return
    if (contractData.value === undefined) {
        return
    }

    activeSection.value = getEditingActiveSection(
        contractData.value.draftExpiresAtUtc,
        contractData.value.readyForPurchase
    )
}
// Run our function on setup
setEditingActiveSection()

const setCartData = useSetCartData()
const { mutate: createContract, isPending: isCreatingContract } =
    useMembershipCreateContractMutation()
function handleCreateContract(input: CreateContractInput) {
    createContract(input, {
        onSuccess: data => {
            contractData.value = getFragmentData(
                PurchaseMembershipContractFragment,
                data
            )
            activeSection.value = 1
        }
    })
}

const { mutate: changeContract, isPending: isChangingContract } =
    useMembershipChangeContractMutation()
function handleChangeContract(input: ChangeContractInput) {
    changeContract(input, {
        onSuccess: data => {
            if (data.changeContract.__typename === 'ChangeContractResult') {
                // Update the cart data
                if (data.changeContract.query.viewer !== null) {
                    setCartData(data.changeContract.query.viewer.cart)
                }
                if (data.changeContract.record.__typename === 'PlanContract') {
                    contractData.value = getFragmentData(
                        PurchaseMembershipContractFragment,
                        data.changeContract.record
                    )
                    activeSection.value = 1
                    return
                }
            }

            throw new Error()
        }
    })
}

const { mutateAsync: updateContract, isPending: isUpdatingContract } =
    useMembershipUpdateContractMutation()
const queryClient = useQueryClient()
function handleContinue(mutationData: UpdateContractInput) {
    updateContract(mutationData, {
        onSuccess: data => {
            if (data.updateContract.__typename === 'UpdateContractResult') {
                // Update the cart data
                if (data.updateContract.query.viewer !== null) {
                    setCartData(data.updateContract.query.viewer.cart)
                }
                if (data.updateContract.record.__typename === 'PlanContract') {
                    contractData.value = getFragmentData(
                        PurchaseMembershipContractFragment,
                        data.updateContract.record
                    )
                    activeSection.value = 2
                    queryClient.invalidateQueries({
                        predicate: query =>
                            query.queryKey.includes('EditDraftMembershipQuery')
                    })
                    return
                }
            }

            throw new Error()
        }
    })
}

function handleAddToCartParticipantDetails(input: UpdateContractInput) {
    updateContract(input, {
        onSuccess: data => {
            if (data.updateContract.__typename === 'UpdateContractResult') {
                // Update the cart data
                if (data.updateContract.query.viewer !== null) {
                    setCartData(data.updateContract.query.viewer.cart)
                }

                if (data.updateContract.record.__typename === 'PlanContract') {
                    emitAddToCartEvent(data.updateContract.record)
                }

                addToCart()
                return
            }

            throw new Error()
        }
    })
}

const showAlert = useShowAlert()
const toast = useToast()
const { mutate: activateContractMutate, isPending: isActivatingContract } =
    useActivateContractMutation()
async function handleActivateContractNow(input: UpdateContractInput) {
    await updateContract(input, {
        onSuccess: data => {
            if (
                data.updateContract.__typename === 'UpdateContractResult' &&
                data.updateContract.record.__typename === 'PlanContract'
            ) {
                if (data.updateContract.record.__typename === 'PlanContract') {
                    contractData.value = getFragmentData(
                        PurchaseMembershipContractFragment,
                        data.updateContract.record
                    )
                    return
                }
            }

            throw new Error()
        }
    })

    if (contractData.value === undefined) {
        throw new Error()
    }

    activateContractMutate(
        {
            id: contractData.value.id,
            updatedAtUtc: contractData.value.updatedAtUtc,
            documentInput: null
        },
        {
            onSuccess: data => {
                if (
                    data.__typename === 'ActivateContractResult' ||
                    data.__typename === 'ContractIsActive'
                ) {
                    toast.add({
                        severity: 'success',
                        summary: 'Thank you for your purchase!',
                        detail: planTitle.value,
                        life: 10000,
                        closable: false
                    })
                    router.push(storefrontHomeLink(storefrontPrefix.value))
                } else {
                    showAlert(
                        'Whoops!',
                        'Something was not quite right. Please try again and if the issue persists, contact the facility to resolve'
                    )
                }
            }
        }
    )
}

const invalidInitials = ref(new Set<number>())
const invalidSignatures = ref(new Set<number>())

const { mutate: signDocument, isPending: isSigningDocument } =
    useMembershipSignDocumentMutation()
function handleSignDocument(mutationData: SignDocumentInput) {
    signDocument(mutationData, {
        onSuccess: data => {
            if (data.signDocument.__typename === 'ValidationError') {
                handleValidationErrors(
                    data.signDocument.fieldErrors,
                    invalidInitials,
                    invalidSignatures
                )
                return
            }

            if (data.signDocument.__typename === 'SignDocumentResult') {
                // Update the cart data
                if (data.signDocument.query.viewer !== null) {
                    setCartData(data.signDocument.query.viewer.cart)
                }

                if (data.signDocument.record.__typename === 'PlanContract') {
                    contractData.value = getFragmentData(
                        PurchaseMembershipContractFragment,
                        data.signDocument.record
                    )
                    return
                }
            }

            throw new Error()
        }
    })
}

const router = useRouter()
const storefrontPrefix = useStorefrontPrefix()
const { storefrontHomeLink } = useRouteLink()
function addToCart() {
    setCartVisible(true)
    router.push(storefrontHomeLink(storefrontPrefix.value))
}

watch(
    () => planData.value.id,
    (newValue, oldValue) => {
        if (newValue !== oldValue) {
            // close all the components
            activeSection.value = 0
            // Remove contract data
            contractData.value = undefined
        }
    }
)

useRedirectOnContractDelete(() => contractData.value?.id)
</script>
